import { createReducer } from '@reduxjs/toolkit'

import { TDashboard } from 'types/GlobalDashboard'

import * as actions from '../../actions/Dashboards/actions'
import * as dashboardGroupActions from '../../actions/DashboardGroups/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import * as widgetsActions from '../../actions/Widgets/DashboardWidgets/actions'

import { insertItems, removeItem, upsertItem, upsertItems } from 'redux/utils'

export type TFetchStatus = 'idle' | 'fetching' | 'success' | 'error'

export type TFetchStatusEntry = {
  status: TFetchStatus
  errorStatusCode?: number
}

export interface DashboardReducerType {
  dashboards: {
    [id: string]: TDashboard
  }
  fetchStatus: {
    [id: string]: TFetchStatusEntry
  }
}

// Initial state
const INITIAL_STATE: DashboardReducerType = {
  dashboards: {},
  fetchStatus: {}
}

// Reducer
const dashboardReducer = createReducer(INITIAL_STATE, (builder) => {
  builder

    // tryGetAllDashboardGroups
    .addCase(dashboardGroupActions.getAllSuccess, (state, action) => {
      const allDashboards = action.payload.flatMap((group) => group.dashboards)

      state.dashboards = insertItems(allDashboards)
    })

    // tryGetOneDashboard
    .addCase(actions.getOne, (state, action) => {
      state.fetchStatus[action.payload] = { status: 'fetching' }
    })
    .addCase(actions.getOneSuccess, (state, action) => {
      state.dashboards = upsertItem(
        state.dashboards,
        action.payload as TDashboard
      )
      state.fetchStatus[action.payload.id] = { status: 'success' }
    })
    .addCase(actions.getOneFailed, (state, action) => {
      state.fetchStatus[action.payload.id] = {
        status: 'error',
        errorStatusCode: action.payload.errorStatusCode
      }
    })

    // tryCreateDashboard
    .addCase(actions.createSuccess, (state, action) => {
      const { payload } = action

      state.dashboards[payload.dashboard.id] = payload.dashboard
    })

    // tryGetOnePublicDashboard & tryGetOneApiAccessDashboard
    .addCase(actions.getOnePublic, (state, action) => {
      state.fetchStatus[action.payload] = { status: 'fetching' }
    })
    .addCase(actions.getOnePublicSuccess, (state, action) => {
      state.dashboards = upsertItem(
        state.dashboards,
        action.payload as TDashboard
      )
      state.fetchStatus[action.payload.id] = { status: 'success' }
    })
    .addCase(actions.getOnePublicFailed, (state, action) => {
      state.fetchStatus[action.payload.id] = {
        status: 'error',
        errorStatusCode: action.payload.errorStatusCode
      }
    })

    // tryDeleteDashboard
    .addCase(actions.destroySuccess, (state, action) => {
      state.dashboards = removeItem(state.dashboards, action.payload)
    })

    // tryUpdateDashboard
    .addCase(actions.updateSuccess, (state, action) => {
      state.dashboards = upsertItem(
        state.dashboards,
        action.payload as TDashboard
      )
    })

    // tryMoveDashboard
    .addCase(actions.moveSuccess, (state, action) => {
      state.dashboards = upsertItem(
        state.dashboards,
        action.payload as TDashboard
      )
    })

    // tryDeleteWidget
    .addCase(widgetsActions.destroySuccess, (state, action) => {
      const { payload } = action

      state.dashboards[payload.dashboardId].available_space =
        payload.available_space
    })

    // tryCreateWidget
    .addCase(widgetsActions.createSuccess, (state, action) => {
      const { payload } = action

      state.dashboards[payload.data.dashboard_id].available_space =
        payload.available_space
    })

    // tryPatchCustomFieldValue
    .addCase(actions.patchCustomFieldValueSuccess, (state, action) => {
      const { payload } = action
      const dashboard = state.dashboards[payload.id]
      const index = dashboard.custom_field_values.findIndex(
        (v) => v.custom_field_id === payload.data.custom_field_id
      )

      if (payload.data.value === null) {
        if (index !== -1) {
          dashboard.custom_field_values.splice(index, 1)
        }
      } else if (index !== -1) {
        dashboard.custom_field_values[index] = payload.data
      } else {
        dashboard.custom_field_values.push(payload.data)
      }
    })

    // tryUpdateDashboardLayout
    .addCase(actions.updateWidgetLayoutSuccess, (state, action) => {
      const { payload } = action

      state.dashboards[payload.id].available_space = payload.available_space
    })

    // tryUpdateDashboardOrder
    .addCase(
      dashboardGroupActions.updateDashboardOrderSuccess,
      (state, action) => {
        const { payload } = action

        state.dashboards = upsertItems(state.dashboards, payload.order)
      }
    )

    // tryCopyDashboardGroup
    .addCase(dashboardGroupActions.copySuccess, (state, action) => {
      const { payload } = action

      state.dashboards = upsertItems(state.dashboards, payload.dashboards)
    })

    // tryRestoreDashboardGroup
    .addCase(dashboardGroupActions.restoreSuccess, (state, action) => {
      const { payload } = action

      state.dashboards = upsertItems(state.dashboards, payload.dashboards)
    })

    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default dashboardReducer
